import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

/**
 * Handles return from Stripe (?session_id=...) AND from PayPal (?token=ORDER_ID&PayerID=...)
 */
export default function PremiumSuccess() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("checking"); // checking | success | failed | timeout
  const [plan, setPlan] = useState(null);
  const polledRef = useRef(false);

  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("token");

  useEffect(() => {
    if (polledRef.current) return;
    polledRef.current = true;

    const pollStripe = async (attempt = 0) => {
      const max = 8;
      if (attempt >= max) { setPhase("timeout"); return; }
      try {
        const { data } = await api.get(`/premium/checkout/stripe/status/${sessionId}`);
        if (data.payment_status === "paid" && data.activated) {
          setPlan(data.plan);
          setPhase("success");
          return;
        }
        if (data.status === "expired") {
          setPhase("failed");
          return;
        }
        setTimeout(() => pollStripe(attempt + 1), 2000);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Error verificando pago");
        setPhase("failed");
      }
    };

    const capturePayPal = async () => {
      try {
        const { data } = await api.post(`/premium/paypal/capture/${paypalOrderId}`);
        if (data.activated || data.already_processed) {
          setPlan(data.plan);
          setPhase("success");
        } else {
          setPhase("failed");
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Error capturando pago PayPal");
        setPhase("failed");
      }
    };

    if (sessionId) {
      pollStripe();
    } else if (paypalOrderId) {
      capturePayPal();
    } else {
      setPhase("failed");
    }
  }, [sessionId, paypalOrderId]);

  return (
    <div className="min-h-screen bg-ink relative overflow-hidden">
      <SEO title="Pago · TestOpoGuardiaCivil" description="Procesando tu pago Premium" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-40" />
      <div className="relative">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-24 text-center">
          {phase === "checking" && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Loader2 className="h-14 w-14 text-gcgold mx-auto animate-spin" />
              <h1 className="mt-6 font-display text-3xl font-extrabold text-white tracking-tighter">Verificando tu pago...</h1>
              <p className="mt-3 text-slate-400">No cierres la ventana. Solo tardará unos segundos.</p>
            </motion.div>
          )}

          {phase === "success" && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} data-testid="premium-success">
              <div className="h-20 w-20 rounded-2xl bg-gcgold/20 mx-auto flex items-center justify-center">
                <Crown className="h-10 w-10 text-gcgold" />
              </div>
              <h1 className="mt-6 font-display text-4xl font-extrabold text-white tracking-tighter">¡Bienvenido a Premium!</h1>
              <p className="mt-3 text-slate-300">Tu plan <strong className="text-gcgold uppercase">{plan}</strong> ya está activo. Disfruta de todas las ventajas.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/dashboard")} className="bg-gcgreen hover:bg-gcgreen-light text-white font-bold rounded-xl" data-testid="goto-dashboard">
                  Ir al panel
                </Button>
                <Button onClick={() => navigate("/tests")} variant="outline" className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl">
                  Empezar a entrenar
                </Button>
              </div>
            </motion.div>
          )}

          {phase === "failed" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="premium-failed">
              <div className="h-20 w-20 rounded-2xl bg-rose-500/20 mx-auto flex items-center justify-center">
                <AlertTriangle className="h-10 w-10 text-rose-400" />
              </div>
              <h1 className="mt-6 font-display text-3xl font-extrabold text-white tracking-tighter">El pago no se completó</h1>
              <p className="mt-3 text-slate-400">No te hemos cobrado nada. Puedes intentarlo de nuevo cuando quieras.</p>
              <Button onClick={() => navigate("/premium")} className="mt-6 bg-gcgold hover:bg-gcgold-light text-ink font-bold rounded-xl">
                Volver a planes
              </Button>
            </motion.div>
          )}

          {phase === "timeout" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Loader2 className="h-14 w-14 text-slate-500 mx-auto" />
              <h1 className="mt-6 font-display text-3xl font-extrabold text-white tracking-tighter">Estamos procesando el pago</h1>
              <p className="mt-3 text-slate-400">Esto suele tardar muy poco. Refresca esta página o consulta tu estado en el panel.</p>
              <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-gcgreen hover:bg-gcgreen-light text-white font-bold rounded-xl">
                Ir al panel
              </Button>
            </motion.div>
          )}
        </main>
        <Footer />
      </div>
    </div>
  );
}
